/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import React, { useContext, useReducer, useEffect } from 'react';

import { useParams, useHistory } from 'react-router-dom';
import { usePreviewDate } from '../common/usePreviewDate';

import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';
import ProductAdminDataSource from './ProductAdminDataSource';
import {
    generateUrlFromState,
    convertStateToHistoryState,
    restoreStateFromHistoryState,
    extractStateFromUrl
} from './routingUtils';

export const ProductAdminContext = React.createContext();

const createReducer = props => {
    const { history } = props;
    return (state, action) => {
        let newState;

        switch (action.type) {
            case 'setConfig':
                if (action.config) {
                    // Select config or go back to root category
                    newState = {
                        ...state,
                        config: action.config,
                        categories: [],
                        configurableProduct: null,
                        searchTerm: null
                    };
                } else {
                    // Go back to category selection
                    newState = {
                        ...state,
                        config: action.config,
                        categories: [],
                        configurableProduct: null,
                        searchTerm: null,
                        previewDate: null
                    };
                }
                break;
            case 'setCategories':
                newState = {
                    ...state,
                    categories: action.categories,
                    configurableProduct: null
                };
                break;
            case 'setConfigurableProduct':
                newState = {
                    ...state,
                    configurableProduct: action.product
                };
                break;
            case 'setSortField':
                newState = {
                    ...state,
                    sortField: action.sortField
                };
                break;
            case 'setSortDirection':
                newState = {
                    ...state,
                    sortDirection: action.sortDirection
                };
                break;
            case 'setSearchTerm':
                newState = {
                    ...state,
                    searchTerm: action.searchTerm,
                    previewDate: null,
                    categories: [],
                    configurableProduct: null
                };
                break;
            case 'setPreviewDate':
                newState = {
                    ...state,
                    previewDate: action.previewDate,
                    searchTerm: null,
                    categories: [],
                    configurableProduct: null
                };
                break;
            case 'setState':
                newState = { ...state, ...action.state };
                break;
            default:
                newState = state;
        }

        // Generate new url from new state and push it, if different
        const newUrl = generateUrlFromState(newState);
        if (history.location.pathname !== newUrl) {
            history.push(newUrl, convertStateToHistoryState(newState));
        }

        newState.dataSource.setState(newState);
        return newState;
    };
};

const ProductAdminContextProvider = props => {
    const params = useParams();
    const history = useHistory();
    const [schemaStatus, , configs] = useGraphqlSchemaContext();

    const defaultState = {
        config: null,
        configs: [],
        categories: [],
        configurableProduct: null,
        sortField: 'name',
        sortDirection: 1, // 1 = asc, 0 = desc
        previewDate: null,
        searchTerm: null,
        loading: true
    };

    const [state, dispatch] = useReducer(createReducer({ history }), {
        ...defaultState,
        dataSource: new ProductAdminDataSource({ state: defaultState })
    });
    const [, { setPreviewDate: setPreviewDateGlobal }] = usePreviewDate();

    const setConfig = config => {
        dispatch({
            type: 'setConfig',
            config
        });
    };

    const setCategories = categories => {
        dispatch({
            type: 'setCategories',
            categories
        });
    };

    const setConfigurableProduct = product => {
        dispatch({
            type: 'setConfigurableProduct',
            product
        });
    };

    const setSortField = sortField => {
        dispatch({
            type: 'setSortField',
            sortField
        });
    };

    const setSortDirection = sortDirection => {
        dispatch({
            type: 'setSortDirection',
            sortDirection
        });
    };

    const setSearchTerm = searchTerm => {
        dispatch({
            type: 'setSearchTerm',
            searchTerm: searchTerm ? searchTerm : null
        });
    };

    const setPreviewDate = async previewDate => {
        await setPreviewDateGlobal(previewDate);
        dispatch({
            type: 'setPreviewDate',
            previewDate
        });
    };

    const navigate = url => {
        history.push(url, convertStateToHistoryState(state));
    };

    useEffect(() => {
        // Wait until schema is loaded and verified. This is required, because when loading the console from URL,
        // extractStateFromUrl is called and it will require information about the schema.
        if (schemaStatus !== 'done') {
            return;
        }

        // Listen to history changes
        const unlisten = history.listen((location, action) => {
            // Only act on POP actions which are triggered for clicking the back/forward button in the browser.
            if (action !== 'POP') return;

            // If no state is set, set state to default
            const newState = location.state
                ? { ...restoreStateFromHistoryState(configs, location.state), loading: false }
                : { ...defaultState, configs, loading: false };
            dispatch({ type: 'setState', state: newState });
        });

        // Try to restore state from URL
        extractStateFromUrl(state, dispatch, configs, params);

        return () => {
            unlisten();
        };
    }, [schemaStatus, configs]);

    const contextValue = [
        state,
        {
            setConfig,
            setCategories,
            setConfigurableProduct,
            setSortField,
            setSortDirection,
            setPreviewDate,
            setSearchTerm,
            navigate
        }
    ];

    return <ProductAdminContext.Provider value={contextValue}>{props.children}</ProductAdminContext.Provider>;
};

export default ProductAdminContextProvider;

export const useProductAdminContext = () => useContext(ProductAdminContext);
